import { createFileRoute } from '@tanstack/react-router';
import { assertPublicToolsEnabled } from '@/lib/public-tools-feature';
import { LanguageToolPage } from '@/components/astro/new-design-25/SEO/language-tool-page';
import { hreflangLinks, seo } from '@/seo/seo';

const faqTitle = 'Portuguese text to speech FAQ';
const faqDescription =
  'Quick answers about converting Portuguese text to speech with Listening.';
const faqs = [
  {
    question: 'Which Portuguese voices are available?',
    answer:
      'Listening offers natural Portuguese voices in male and female options, covering both Brazilian Portuguese and European Portuguese variants so audio matches the audience.',
  },
  {
    question: 'How does it handle nasal vowels and accents?',
    answer:
      'Portuguese nasal vowels (ã, õ, etc.) and accented vowels are produced naturally, with correct stress and the right rhythm for Brazilian or European Portuguese contexts.',
  },
  {
    question: 'Can I use this for academic Portuguese content?',
    answer:
      'Yes. The voices are trained on Portuguese academic, literary, and journalistic content, with proper handling of academic vocabulary and formal register.',
  },
  {
    question: 'Can I use this for Portuguese language learning?',
    answer:
      'Yes. Slow the playback for tough passages, then speed up as your listening improves. Synced highlighting in the Listening app helps connect sound to written Portuguese.',
  },
];

const heroTitle = 'Portuguese Text to Speech';
const heroDescription =
  'Portuguese text to speech for academic content. Natural Brazilian and European Portuguese voices. Perfect for research papers and educational materials.';

const featureTitle = 'the Portuguese academic audio solution that helps:';
const featureSubtitle = '';
const featureDescription = '';
const featureBadge = 'Multilingual';
const featureVariant = 'default';
const featureCtaText = 'Try now';

const featureFeatures = [
  {
    title: 'Native Language Access',
    description:
      'Brazilian and Portuguese students access academic content in their native language',
  },
  {
    title: 'International Collaboration',
    description:
      'International researchers collaborate with Portuguese-speaking institutions',
  },
  {
    title: 'Accessibility Content',
    description:
      'Universities create Portuguese course materials and accessibility content',
  },
  {
    title: 'Multilingual Research',
    description:
      'Portuguese-speaking academics process multilingual research efficiently',
  },
];

const solutionBenefitsData = {
  title: 'Feature Bullets',
  items: [
    {
      text: 'Natural Brazilian and European Portuguese voices for academic content',
      highlight: 'Brazilian and European Portuguese',
    },
    {
      text: 'Perfect pronunciation of Portuguese scientific and technical terminology',
      highlight: 'Perfect pronunciation',
    },
    {
      text: 'Specialized training on Portuguese academic and educational vocabulary',
      highlight: 'Specialized training',
    },
    {
      text: 'Seamless processing of Portuguese research papers and course materials',
      highlight: 'Seamless processing',
    },
  ],
};

const useCasesData = {
  title: 'Use Case Scenarios',
  description:
    'Real-world scenarios where Portuguese text-to-speech transforms academic accessibility and research productivity.',
  useCases: [
    {
      title: 'Brazilian University Student',
      challenge:
        'Access international research papers with Portuguese language support',
      solutionSections: [
        {
          label: 'Portuguese TTS Solution',
          content:
            'Natural Brazilian Portuguese voices with proper academic pronunciation',
        },
        {
          label: 'Generic TTS Problem',
          content:
            'Generic Portuguese TTS struggles with academic vocabulary and mispronounces technical terms',
        },
      ],
    },
    {
      title: 'European Research Institution',
      challenge:
        'Create accessible Portuguese versions of research materials for diverse audiences',
      solutionSections: [
        {
          label: 'Portuguese TTS Solution',
          content:
            'European Portuguese voices optimized for academic content delivery',
        },
        {
          label: 'Generic TTS Problem',
          content:
            'Standard TTS lacks proper pronunciation of Portuguese academic terminology',
        },
      ],
    },
    {
      title: 'International Academic Collaboration',
      challenge:
        'Bridge language barriers between Portuguese and international research communities',
      solutionSections: [
        {
          label: 'Portuguese TTS Solution',
          content:
            'Accurate Portuguese academic vocabulary with technical term pronunciation',
        },
        {
          label: 'Generic TTS Problem',
          content:
            'Poor pronunciation makes Portuguese academic content difficult to understand',
        },
      ],
    },
    {
      title: 'Portuguese Course Content Creator',
      challenge:
        'Develop high-quality Portuguese educational materials for online learning',
      solutionSections: [
        {
          label: 'Portuguese TTS Solution',
          content:
            'Professional Portuguese voices suitable for educational content delivery',
        },
        {
          label: 'Generic TTS Problem',
          content:
            'Basic Portuguese TTS lacks the quality needed for professional educational content',
        },
      ],
    },
  ],
};

const seoSections = [
  {
    title: 'Why Portuguese Academic Content Needs Specialized Text-to-Speech',
    content:
      'Portuguese-speaking academic communities require text-to-speech technology that understands both Brazilian Portuguese and European Portuguese linguistic variations, especially for technical and scientific terminology that differs between regions. Generic Portuguese TTS systems often struggle with academic vocabulary, mispronouncing scientific terms, institutional names, and technical concepts that are crucial for educational content comprehension.',
    contentSecondary:
      'Our Portuguese text-to-speech system provides natural-sounding voices specifically trained on academic Portuguese content from both Brazil and Portugal. We achieve superior accuracy on scientific terminology, proper pronunciation of Portuguese academic institutions, and natural handling of complex sentence structures common in scholarly Portuguese writing. This specialized approach is essential for Portuguese-speaking students, researchers, and educational institutions requiring professional-quality Portuguese audio content.',
  },
  {
    title: 'Bridge Language Barriers in Portuguese Academic Research',
    content:
      'Our Portuguese TTS technology serves the growing community of Portuguese-speaking researchers and students who need access to both Portuguese academic content and Portuguese translations of international research. The system handles complex academic Portuguese vocabulary, maintains proper pronunciation of technical terms across different Portuguese variants, and provides the natural speech patterns that make extended academic listening comfortable and effective.',
    contentSecondary:
      'Portuguese academic users consistently report improved comprehension and accessibility when using specialized Portuguese TTS compared to generic translation tools. Whether processing research papers from Brazilian universities, European Portuguese academic journals, or creating accessible Portuguese educational content, our system delivers the linguistic accuracy and natural flow essential for serious academic work in Portuguese-speaking contexts.',
  },
];

export const Route = createFileRoute(
  '/_marketing/_seo/portuguese-text-to-speech',
)({
  beforeLoad: assertPublicToolsEnabled,
  head: () => ({
    meta: [
      ...seo({
        title:
          'Portuguese Text to Speech — Natural Brazilian & European Portuguese Voices | Listening',
        description:
          'Portuguese text to speech with natural Brazilian and European Portuguese AI voices. Built for academic content, language learning, and professional audio. Free demo.',
      }),
    ],
    links: [...hreflangLinks('/portuguese-text-to-speech')],
  }),
  component: () => (
    <LanguageToolPage
      converterHeading={heroTitle}
      converterSubtitle={heroDescription}
      faqDescription={faqDescription}
      faqTitle={faqTitle}
      faqs={faqs}
      featureBadge={featureBadge}
      featureCtaText={featureCtaText}
      featureDescription={featureDescription}
      featureFeatures={featureFeatures}
      featureSubtitle={featureSubtitle}
      featureTitle={featureTitle}
      featureVariant={featureVariant}
      preferredLang='pt'
      seoSections={seoSections}
      solutionBenefitsData={solutionBenefitsData}
      useCasesData={useCasesData}
    />
  ),
});
